import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LockKey, ShieldCheck, Eye, EyeSlash, FloppyDisk } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, formatApiErrorDetail } from '@/auth/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function PwInput({ id, value, onChange, placeholder, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <LockKey size={16} weight="duotone" className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        autoComplete="new-password"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="pl-9 pr-9 h-10"
        placeholder={placeholder}
        data-testid={testid}
      />
      <button
        type="button"
        onClick={() => setShow((v) => !v)}
        tabIndex={-1}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-700 rounded"
      >
        {show ? <EyeSlash size={16} /> : <Eye size={16} />}
      </button>
    </div>
  );
}

export default function ChangePassword() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const forced = !!(user && typeof user === 'object' && user.must_change_password);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) return setError('新しいパスワードは8文字以上で入力してください。');
    if (next !== confirm) return setError('新しいパスワードが一致しません。');
    if (!forced && !current) return setError('現在のパスワードを入力してください。');
    setLoading(true);
    try {
      if (forced) {
        await axios.post(`${API}/auth/set-initial-password`, { new_password: next });
      } else {
        await axios.post(`${API}/auth/change-password`, { current_password: current, new_password: next });
      }
      await refreshMe();
      toast.success('パスワードを変更しました');
      navigate('/', { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'パスワードの変更に失敗しました。');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 bg-zinc-50 overflow-hidden">
      <div className="pointer-events-none absolute -top-24 -left-24 w-96 h-96 bg-violet-200/40 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-32 w-[28rem] h-[28rem] bg-blue-200/40 rounded-full blur-3xl" />

      <div className="relative w-full max-w-md">
        <div className="text-center mb-6">
          <span className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-zinc-100 text-zinc-700 shadow-sm mb-3">
            <ShieldCheck size={24} weight="duotone" />
          </span>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-900">
            {forced ? 'パスワードの変更が必要です' : 'パスワードの変更'}
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            {forced
              ? '初回ログインのため、新しいパスワードを設定してください。'
              : 'アカウントのパスワードを変更します。'}
          </p>
        </div>

        <form
          onSubmit={onSubmit}
          className="bg-white rounded-lg border border-zinc-200 shadow-sm p-7 space-y-5"
          data-testid="change-password-form"
        >
          {user && typeof user === 'object' && (
            <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-600">
              ログイン中: <span className="font-semibold text-zinc-900">{user.email}</span>
            </div>
          )}

          {!forced && (
            <div className="space-y-1.5">
              <Label htmlFor="current" className="text-xs font-semibold text-zinc-700">現在のパスワード</Label>
              <PwInput id="current" value={current} onChange={setCurrent} placeholder="現在のパスワード" testid="cp-current" />
            </div>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="next" className="text-xs font-semibold text-zinc-700">新しいパスワード (8文字以上)</Label>
            <PwInput id="next" value={next} onChange={setNext} placeholder="新しいパスワード" testid="cp-new" />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="confirm" className="text-xs font-semibold text-zinc-700">新しいパスワード (確認)</Label>
            <PwInput id="confirm" value={confirm} onChange={setConfirm} placeholder="もう一度入力" testid="cp-confirm" />
          </div>

          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2" data-testid="cp-error" role="alert">
              {error}
            </p>
          )}

          <Button
            type="submit"
            disabled={loading || !next || !confirm || (!forced && !current)}
            className="w-full h-11 bg-teal-700 hover:bg-teal-800 text-white"
            data-testid="cp-submit"
          >
            <FloppyDisk size={16} weight="bold" className="mr-2" />
            {loading ? '保存中…' : 'パスワードを変更'}
          </Button>
        </form>

        <p className="text-center text-[11px] text-zinc-400 mt-6">© 2026 株式会社センスビー</p>
      </div>
    </div>
  );
}
